import { useState, useRef, useEffect } from "react";

// ASSETS

import shareIcon from "assets/svg/ShareIcon.svg";
import ArrowUpIcon from "assets/svg/RightRedIcon.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import facebookIcon from "assets/svg/FacebookGrey.svg";
import linkedInIcon from "assets/svg/LinkedInGrey.svg";
import DsuLogo from "assets/img/Logo-DSU@2x.png";
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";

// STYLES
import {
  SingleGuideContainer,
  HeaderImageWrapper,
  HeaderImage,
  Body,
  FirstColumn,
  SecondColumn,
  ThirdColumn,
  BackToButton,
  BackToGuidesImage,
  BackToWrapper,
  TitleWrapper,
  ContentCard,
  SocialMediaCard,
  SocialMediaIcons,
  SocialMediaIcon,
  OtherGuidesCard,
  ImgStyled,
  OtherChapters,
  InsideFirstColumn,
  InsideThirdColumn,
  BackOnTopWrapper,
  BackOnTopImg,
  LeftCard,
  OtherCampaignsContent,
  CopyToClipboardWrapper,
  SkeletonWrapperOtherChapter,
  CopyToClipboardTooltip,
  SkeletonWrapperChapter,
  ImgLeftCard,
  CenterLeftCard,
  WrapperTexts,
  TextWrapperOtherArticles,
} from "./OfficialAnnouncementsDetailsStyled";

// LIBRARIES
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import { LinkedinShareButton, FacebookShareButton } from "react-share";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

// MISC

import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { BORDER_RADIUS, COLORS, DEVICE_TYPE } from "constants/variables";
import { appRoutes } from "config/AppRoutesConfig";
import formatDate from "utils/formatDate";

// REDUX

import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import OfficialsCarousel from "components/OfficialsCarousel/OfficialsCarousel";
import newsAndMediaService from "services/newsAndMediaService";
import { uploadBaseUrl } from "services/base";
import {
  CenterCardTitle,
  ScrollTargetWrapper,
} from "view/CampaignDetails/CampaignDetailsStyled";
import StrapiTreeText from "components/Atoms/StrapiTreeText/StrapiTreeText";

const OfficialAnnouncementsDetails = () => {
  // PROPERTIES

  // LIBRARY CONSTANTS
  const isMobile = useMediaQuery({ maxWidth: DEVICE_TYPE.mobileHeader });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const { id } = useParams<{ id: string }>();

  // STATE CONSTANTS
  const contentRefs = useRef([]);
  const skeletonCount = 3;
  const [copied, setCopied] = useState<boolean>(false);
  const headerImageWrapperRef = useRef(null);
  const [officialAnnouncements, setOfficialAnnouncements] = useState<any>();
  const [otherOfficialAnnouncements, setOtherOfficialAnnouncements] =
    useState<any[]>();

  // API REQUESTS

  // LIFE CYCLE

  // EVENT HANDLERS

  const scrollToTop = () => {
    if (headerImageWrapperRef.current) {
      headerImageWrapperRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleCopyToClipboard = () => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => console.error("Failed to copy URL: ", err));
    } else {
      // Fallback method using older APIs
      const textArea = document.createElement("textarea");
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    // setLoading(true);
    newsAndMediaService
      .getNewsArticlesById(id)
      .then((response) => {
        // console.log("getNewsArticlesById resp", response);
        setOfficialAnnouncements(response.data);
        // setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        // setLoading(false);
      });
    newsAndMediaService
      .getNewsArticlesByCategory(1, 3, "", 1)
      .then((response) => {
        // console.log("getNewsArticles resp", response);
        setOtherOfficialAnnouncements(response.data);
        // setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        // setLoading(false);
      });
  }, [id]);

  return (
    <SingleGuideContainer>
      <HeaderImageWrapper ref={headerImageWrapperRef}>
        <HeaderImage
          src={
            officialAnnouncements?.attributes?.cover?.data?.attributes?.url?.trim()
              ? `${uploadBaseUrl}${officialAnnouncements?.attributes?.cover?.data?.attributes?.url}`
              : CoverPlaceholder
          }
          loading="lazy" decoding="async"
          alt={"coverGuideImage"}
        />
      </HeaderImageWrapper>
      <Body>
        <FirstColumn>
          <InsideFirstColumn>
            <BackToWrapper
              onClick={() => navigate(appRoutes.officials)}
              biggerCursor={biggerCursor}
            >
              <BackToGuidesImage src={LeftArrow} alt="backIcon" />
              <BackToButton biggerCursor={biggerCursor}>
                <CustomText
                  text={t(
                    "view.newsAndMedia.officialAnnouncements.innerBackButton"
                  )}
                  type={TextType.H5boldspaced}
                  color={COLORS.RaisinBlack}
                />
              </BackToButton>
            </BackToWrapper>
            <TitleWrapper>
              <CustomText
                text={officialAnnouncements?.attributes?.title}
                type={TextType.H3}
              />
            </TitleWrapper>
            <LeftCard>
              <ImgLeftCard src={DsuLogo} />
              <WrapperTexts>
                <CenterLeftCard>
                  <CustomText
                    text={
                      officialAnnouncements?.attributes?.article_date
                        ? formatDate(
                            officialAnnouncements?.attributes?.article_date,
                            null,
                            "ro"
                          )
                        : formatDate(
                            officialAnnouncements?.attributes?.createdAt,
                            null,
                            "ro"
                          )
                    }
                    type={TextType.H5}
                    color={COLORS.VividAuburn}
                  />
                  <CustomText
                    text={officialAnnouncements?.attributes?.author?.data?.attributes?.name}
                    type={TextType.H5}
                    color={COLORS.MetalicSilver}
                  />
                </CenterLeftCard>
              </WrapperTexts>
            </LeftCard>
          </InsideFirstColumn>
        </FirstColumn>
        <SecondColumn>
          {officialAnnouncements?.attributes?.content ? (
            <ScrollTargetWrapper
              ref={contentRefs.current[0]}
              // key={index}
              id={officialAnnouncements?.id}
            >
              <ContentCard tabIndex={0}>
                <CenterCardTitle>
                  {/* <CustomText text={event?.attributes?.event_chapters?.data[0]?.attributes?.title} type={TextType.H3} /> */}
                </CenterCardTitle>
                <StrapiTreeText
                  text={officialAnnouncements?.attributes?.content}
                />
              </ContentCard>
            </ScrollTargetWrapper>
          ) : (
            <ContentCard tabIndex={0}>
              <Skeleton
                width={"100%"}
                count={105}
                baseColor={COLORS.Background}
                highlightColor={COLORS.Cultured}
              />
            </ContentCard>
          )}
        </SecondColumn>
        <ThirdColumn>
          {!isMobile && (
            <InsideThirdColumn>
              <SocialMediaCard>
                <CustomText
                  text={"SHARE"}
                  type={TextType.H5}
                  color={COLORS.Yellow}
                />
                <SocialMediaIcons>
                  <FacebookShareButton url={window.location.href}>
                    <SocialMediaIcon
                      src={facebookIcon}
                      alt="facebookIcon"
                      biggerCursor={biggerCursor}
                    />
                  </FacebookShareButton>
                  <LinkedinShareButton url={window.location.href}>
                    <SocialMediaIcon
                      src={linkedInIcon}
                      alt="linkedInIcon"
                      biggerCursor={biggerCursor}
                    />
                  </LinkedinShareButton>
                  <CopyToClipboardWrapper onClick={handleCopyToClipboard}>
                    <SocialMediaIcon
                      src={shareIcon}
                      alt="shareIcon"
                      biggerCursor={biggerCursor}
                    />
                    {copied && (
                      <CopyToClipboardTooltip>
                        <CustomText
                          text={t(
                            "view.newsAndMedia.officialAnnouncementsDetails.copyClipboard"
                          )}
                          type={TextType.P2}
                          color={COLORS.RaisinBlack}
                        />
                      </CopyToClipboardTooltip>
                    )}
                  </CopyToClipboardWrapper>
                </SocialMediaIcons>
              </SocialMediaCard>
              <OtherGuidesCard>
                <CustomText
                  text={t(
                    "view.newsAndMedia.officialAnnouncementsDetails.others"
                  )}
                  type={TextType.H3}
                />
                {otherOfficialAnnouncements?.length > 0 ? (
                  otherOfficialAnnouncements
                    .slice(0, 3)
                    .map((item: any, index: number) => (
                      <OtherChapters key={index} biggerCursor={biggerCursor}>
                        <ImgStyled
                          src={
                            item?.attributes?.cover?.data?.attributes?.url?.trim()
                              ? `${uploadBaseUrl}${item?.attributes?.cover?.data?.attributes?.url}`
                              : CoverPlaceholder
                          }
                          alt={"imageOthercampaigns"}
                          onClick={() =>
                            navigate(appRoutes.officialsDetails + item.id)
                          }
                        />
                        <OtherCampaignsContent
                          onClick={() =>
                            navigate(appRoutes.officialsDetails + item.id)
                          }
                        >
                          <TextWrapperOtherArticles>
                            <CustomText
                              text={item?.attributes?.title}
                              type={TextType.H4officials}
                            />
                          </TextWrapperOtherArticles>
                        </OtherCampaignsContent>
                      </OtherChapters>
                    ))
                ) : (
                  <>
                    {[...Array(skeletonCount)].map((_, index) => (
                      <SkeletonWrapperOtherChapter key={index}>
                        <Skeleton
                          width={"80px"}
                          height={"80px"}
                          borderRadius={BORDER_RADIUS.radius16}
                          baseColor={COLORS.Background}
                          highlightColor={COLORS.Cultured}
                        />
                        <SkeletonWrapperChapter>
                          <Skeleton
                            count={1}
                            baseColor={COLORS.Background}
                            highlightColor={COLORS.Cultured}
                          />
                        </SkeletonWrapperChapter>
                      </SkeletonWrapperOtherChapter>
                    ))}
                  </>
                )}
              </OtherGuidesCard>
            </InsideThirdColumn>
          )}
        </ThirdColumn>
      </Body>
      {officialAnnouncements?.attributes?.gallery?.data?.length > 0 && (
        <OfficialsCarousel
          data={officialAnnouncements?.attributes?.gallery?.data?.map(
            (image) => ({
              id: image?.id,
              image: image?.attributes?.url?.trim()
                ? `${uploadBaseUrl}${image.attributes.url}`
                : CoverPlaceholder,
            })
          )}
        />
      )}
      {isMobile && (
        <InsideThirdColumn>
          <SocialMediaCard>
            <CustomText
              text={"SHARE"}
              type={TextType.H5}
              color={COLORS.Yellow}
            />
            <SocialMediaIcons>
              <FacebookShareButton url={window.location.href}>
                <SocialMediaIcon src={facebookIcon} alt="facebookIcon" />
              </FacebookShareButton>
              <LinkedinShareButton url={window.location.href}>
                <SocialMediaIcon src={linkedInIcon} alt="linkedInIcon" />
              </LinkedinShareButton>
              <CopyToClipboardWrapper onClick={handleCopyToClipboard}>
                <SocialMediaIcon src={shareIcon} alt="shareIcon" />
                {copied && (
                  <CopyToClipboardTooltip>
                    <CustomText
                      text={t(
                        "view.newsAndMedia.officialAnnouncementsDetails.copyClipboard"
                      )}
                      type={TextType.P2}
                      color={COLORS.RaisinBlack}
                    />
                  </CopyToClipboardTooltip>
                )}
              </CopyToClipboardWrapper>
            </SocialMediaIcons>
          </SocialMediaCard>
          <OtherGuidesCard>
            <CustomText
              text={t("view.newsAndMedia.officialAnnouncementsDetails.others")}
              type={TextType.H3}
            />
            {otherOfficialAnnouncements?.length > 0 ? (
              otherOfficialAnnouncements
                .slice(0, 3)
                .map((item: any, index: number) => (
                  <OtherChapters key={index} biggerCursor={biggerCursor}>
                    <ImgStyled
                      src={
                        item?.attributes?.cover?.data?.attributes?.url?.trim()
                          ? `${uploadBaseUrl}${item?.attributes?.cover?.data?.attributes?.url}`
                          : CoverPlaceholder
                      }
                      alt={"imageOthercampaigns"}
                      onClick={() =>
                        navigate(appRoutes.officialsDetails + item.id)
                      }
                    />
                    <OtherCampaignsContent
                      onClick={() =>
                        navigate(appRoutes.officialsDetails + item.id)
                      }
                    >
                      <TextWrapperOtherArticles>
                        <CustomText
                          text={item?.attributes?.title}
                          type={TextType.H4officials}
                        />
                      </TextWrapperOtherArticles>
                    </OtherCampaignsContent>
                  </OtherChapters>
                ))
            ) : (
              <>
                {[...Array(skeletonCount)].map((_, index) => (
                  <SkeletonWrapperOtherChapter key={index}>
                    <Skeleton
                      width={"80px"}
                      height={"80px"}
                      borderRadius={BORDER_RADIUS.radius16}
                      baseColor={COLORS.Background}
                      highlightColor={COLORS.Cultured}
                    />
                    <SkeletonWrapperChapter>
                      <Skeleton
                        count={1}
                        baseColor={COLORS.Background}
                        highlightColor={COLORS.Cultured}
                      />
                    </SkeletonWrapperChapter>
                  </SkeletonWrapperOtherChapter>
                ))}
              </>
            )}
          </OtherGuidesCard>
        </InsideThirdColumn>
      )}

      {isMobile && (
        <BackOnTopWrapper onClick={scrollToTop}>
          <BackOnTopImg src={ArrowUpIcon} alt="ArrowUpIcon" />
        </BackOnTopWrapper>
      )}
    </SingleGuideContainer>
  );
};

export default OfficialAnnouncementsDetails;
